import { c as _c } from "react/compiler-runtime";
import { IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { Checkbox } from '@repo/design-system/components/ui/checkbox';
import { Type } from '@repo/design-system/components/ui/type';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import type React from 'react';
import type { ChecklistItem } from './checklist-utils';
interface ChecklistItemRowProps {
  item: ChecklistItem;
  isChecked: boolean;
  isSelected: boolean;
  onToggle: (itemId: string) => void;
  onShowOnMap: (itemId: string) => void;
  /** When true, replaces the checkbox with a remove (X) button that fires `onRemove`. */
  editMode?: boolean;
  onRemove?: (itemId: string) => void;
  removeLabel?: string;
}
export const ChecklistItemRow: React.FC<ChecklistItemRowProps> = t0 => {
  const $ = _c(36);
  const {
    item,
    isChecked,
    isSelected,
    onToggle,
    onShowOnMap,
    editMode: t1,
    onRemove,
    removeLabel
  } = t0;
  const editMode = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["game_map"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useT(t2);
  const t3 = item.id;
  let t4;
  let t5;
  if ($[1] !== item.id || $[2] !== onShowOnMap) {
    t4 = e => {
      e.stopPropagation();
      onShowOnMap(item.id);
    };
    t5 = e_0 => {
      if (e_0.key === "Enter" || e_0.key === " ") {
        e_0.preventDefault();
        e_0.stopPropagation();
        onShowOnMap(item.id);
      }
    };
    $[1] = item.id;
    $[2] = onShowOnMap;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  if ($[5] !== isSelected) {
    t6 = css.raw({
      padding: "8",
      borderRadius: "8",
      backgroundColor: isSelected ? "whiteAlpha.10" : undefined,
      _hover: {
        cursor: "pointer",
        backgroundColor: "whiteAlpha.10"
      },
      _focusWithin: {
        backgroundColor: "whiteAlpha.10"
      }
    });
    $[5] = isSelected;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== editMode || $[8] !== isChecked || $[9] !== item.id || $[10] !== item.title || $[11] !== onRemove || $[12] !== onToggle || $[13] !== removeLabel || $[14] !== t) {
    t7 = editMode ? <IconButton onClick={e_1 => {
      e_1.stopPropagation();
      onRemove?.(item.id);
    }} color="secondary" size="sm" aria-label={removeLabel ?? item.title} className={css({
      marginLeft: "2"
    })}><Icon icon="close" decorative={true} /></IconButton> : <Checkbox id={item.id} size="sm" className={css({
      marginLeft: "4",
      display: "flex"
    })} checked={isChecked} onChange={e_2 => {
      e_2.stopPropagation();
      onToggle(item.id);
    }} onKeyDown={e_3 => {
      if (e_3.key === "Enter") {
        e_3.preventDefault();
        e_3.stopPropagation();
        onToggle(item.id);
      }
    }} tabIndex={0} aria-label={`${item.title} - ${isChecked ? t(_temp) : t(_temp2)}`} />;
    $[7] = editMode;
    $[8] = isChecked;
    $[9] = item.id;
    $[10] = item.title;
    $[11] = onRemove;
    $[12] = onToggle;
    $[13] = removeLabel;
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = css({
      width: "[56px]",
      height: "[32px]",
      borderRadius: "8",
      overflow: "hidden",
      backgroundColor: "blackAlpha.30",
      flexShrink: "[0]",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    });
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== isChecked || $[18] !== item.imageUrl || $[19] !== item.title || $[20] !== t) {
    t9 = <div className={t8}>{item.imageUrl ? <img loading="lazy" src={item.imageUrl} alt={item.title} className={css({
        width: "full",
        height: "full",
        objectFit: "cover",
        opacity: isChecked ? "[0.5]" : "[1]",
        transition: "[opacity 0.2s ease]"
      })} /> : <Type variant="body-xs" color="text.secondary" className={css({
        opacity: "[0.3]"
      })}>{t(_temp3)}</Type>}</div>;
    $[17] = isChecked;
    $[18] = item.imageUrl;
    $[19] = item.title;
    $[20] = t;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const t10 = item.title;
  const t11 = isChecked ? "[line-through]" : "none";
  const t12 = isChecked ? "[0.6]" : "[1]";
  let t13;
  if ($[22] !== t11 || $[23] !== t12) {
    t13 = css({
      textDecoration: t11,
      opacity: t12
    });
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== item.title || $[26] !== t13) {
    t14 = <Type truncate={true} variant="body-s" color="text.highlight" title={t10} className={t13}>{item.title}</Type>;
    $[25] = item.title;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== item.id || $[29] !== t14 || $[30] !== t4 || $[31] !== t5 || $[32] !== t6 || $[33] !== t7 || $[34] !== t9) {
    t15 = <FlexRow data-item-id={t3} role="button" tabIndex={0} align="center" gap="12" width="full" onClick={t4} onKeyDown={t5} className={t6}>{t7}{t9}{t14}</FlexRow>;
    $[28] = item.id;
    $[29] = t14;
    $[30] = t4;
    $[31] = t5;
    $[32] = t6;
    $[33] = t7;
    $[34] = t9;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  return t15;
};
function _temp($) {
  return $.checklist_pages.completed;
}
function _temp2($_0) {
  return $_0.checklist_pages.not_completed;
}
function _temp3($_1) {
  return $_1.checklist_pages.no_image;
}